/* Fifth & Piedmont V9 "Motion": film playback, wipe transitions and unveil-on-scroll. No dependencies.
   Owner rules: no hover scaling; every moving element can be paused; motion stops under
   prefers-reduced-motion; nothing is hidden when JavaScript is unavailable. */
const reduce = matchMedia('(prefers-reduced-motion: reduce)');
const phone = matchMedia('(max-width: 749px)');
const saveData = Boolean(navigator.connection && navigator.connection.saveData);
// films start only after the page (and so every first-screen poster) has loaded, plus a short pause,
// so a film never competes with the first screen's photographs on a slow connection
const pageSettled = new Promise((resolve) => {
  const go = () => setTimeout(() => (window.requestIdleCallback || setTimeout)(resolve), phone.matches ? 3000 : 1200);
  if (document.readyState === 'complete') go(); else addEventListener('load', go, { once: true });
});

/* Film: a muted, looping clip over its poster. The source loads only near the screen and plays only
   while a third of it is visible (or while its slide is shown). Reduced motion or Save-Data keep the poster. */
class Film extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.held = this.hasAttribute('data-held');
    if (!this.video || reduce.matches || saveData) return;
    this.video.muted = true;
    this.video.addEventListener('playing', () => this.classList.add('is-playing'));
    // first-screen film (data-eager): start as soon as its own poster shows, not after the whole page
    // (owner, 2026-10-06: the first hero film "takes a while"); the poster is the film's first frame
    if (this.hasAttribute('data-eager')) {
      const img = this.querySelector('img');
      this.ready = new Promise((resolve) => {
        if (!img || img.complete) return resolve();
        img.addEventListener('load', resolve, { once: true }); img.addEventListener('error', resolve, { once: true });
        setTimeout(resolve, 2500);
      });
    } else this.ready = pageSettled;
    this.io = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting && e.intersectionRatio >= 0.33; this.sync(); }, { threshold: [0, 0.33, 0.66], rootMargin: '200px 0px' });
    this.io.observe(this);
  }
  disconnectedCallback() { this.io?.disconnect(); }
  load() {
    if (this.loaded) return;
    const d = this.dataset; const dpr = devicePixelRatio || 1; const r = this.getBoundingClientRect();
    let src;
    if (phone.matches) src = d.phone || d.desktop;
    // portrait films (the department hero's 4:5 frame): the phone file is the same picture, so pick by the frame's
    // real size: the light 720p file when it is enough, otherwise 1080p (not by the window width)
    else if (d.phone && r.height > r.width) src = r.height * dpr <= 760 ? d.phone : d.desktop;
    else src = innerWidth * dpr > 1500 ? d.desktop : (d.desktopSmall || d.desktop);
    this.video.src = src;
    this.loaded = true;
  }
  sync() {
    if (!this.video || reduce.matches || saveData) return;
    if (this.visible && !this.held) {
      this.ready.then(() => { if (this.visible && !this.held) { this.load(); this.video.play().catch(() => {}); } });
    } else if (this.loaded) this.video.pause();
  }
  hold(on) { this.held = on; this.sync(); }
  preload() { if (!this.video || reduce.matches || saveData) return; pageSettled.then(() => { this.video.preload = 'auto'; this.load(); this.video.load(); }); } // preload before src, so the whole film buffers
  restart() { if (this.loaded) { try { this.video.currentTime = 0; } catch (e) { /* not seekable yet */ } } }
}
if (!customElements.get('fp-v9-film')) customElements.define('fp-v9-film', Film);

/* Hero: slides change with a wipe (the new film unveils from the right over the old one), every
   interval seconds, with a progress line and a pause button; hover or focus holds; swipe on phones.
   Only the shown slide's film plays. */
class Hero extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-goto]')];
    this.pauseBtn = this.querySelector('[data-pause]');
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 7000;
    this.style.setProperty('--v9-interval', `${this.interval}ms`);
    this.films().forEach((f, k) => f.hold?.(k !== 0));
    if (this.slides.length < 2) return;
    // once the page is idle, fetch the other slides' posters and product photos so a wipe never uncovers a blank
    const warm = () => this.slides.slice(1).forEach((s) => s.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }));
    if (document.readyState === 'complete') setTimeout(warm, 1200); else addEventListener('load', () => setTimeout(warm, 1200), { once: true });
    this.paused = reduce.matches;
    this.dots.forEach((d) => d.addEventListener('click', () => this.go(Number(d.dataset.goto))));
    this.pauseBtn?.addEventListener('click', () => this.setPaused(!this.paused));
    this.addEventListener('mouseenter', () => this.hold(true));
    this.addEventListener('mouseleave', () => this.hold(false));
    this.addEventListener('focusin', () => this.hold(true));
    this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.hold(false); });
    this.addEventListener('keydown', (e) => {
      if (e.target.closest('[role="tablist"]') && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault(); this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1)); this.dots[this.index]?.focus();
      }
    });
    let x0 = null;
    this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0; x0 = null;
      if (Math.abs(dx) > 50) { this.setPaused(true); this.go(this.index + (dx < 0 ? 1 : -1)); }
    });
    this.setPaused(this.paused);
  }
  films() { return this.slides.map((s) => s.querySelector('fp-v9-film')); }
  go(i) {
    const n = this.slides.length;
    const next = (i + n) % n;
    if (next === this.index) return this.restart();
    const prev = this.slides[this.index];
    const films = this.films();
    films[next]?.restart?.(); films[next]?.hold?.(false);
    this.classList.remove('is-initial');
    prev.classList.remove('is-active'); prev.classList.add('is-leaving');
    clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => {
      this.slides.forEach((s, k) => { if (k !== this.index) { s.classList.remove('is-leaving'); films[k]?.hold?.(true); } });
    }, reduce.matches ? 0 : 1300);
    this.slides.forEach((s, k) => {
      const on = k === next;
      s.classList.toggle('is-active', on);
      s.toggleAttribute('inert', !on);
      s.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    this.dots.forEach((d, k) => d.setAttribute('aria-selected', k === next ? 'true' : 'false'));
    this.index = next;
    this.restart();
  }
  restart() {
    clearTimeout(this.timer); clearTimeout(this.prefetch);
    this.classList.remove('is-running'); void this.offsetWidth;
    if (this.paused || this.held) return;
    this.classList.add('is-running');
    this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
    // fetch the next film a few seconds ahead so it is ready when its slide wipes in
    if (!phone.matches) this.prefetch = setTimeout(() => this.films()[(this.index + 1) % this.slides.length]?.preload?.(), Math.max(0, this.interval - 3500));
  }
  hold(on) { this.held = on; this.classList.toggle('is-held', on); this.restart(); }
  setPaused(on) {
    this.paused = on;
    this.classList.toggle('is-paused', on);
    this.films()[this.index]?.hold?.(on);
    if (this.pauseBtn) {
      this.pauseBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      this.pauseBtn.setAttribute('aria-label', on ? 'Play slideshow' : 'Pause slideshow');
    }
    this.restart();
  }
}
if (!customElements.get('fp-v9-hero')) customElements.define('fp-v9-hero', Hero);

/* Wipe: editorial photographs replace one another with the same right-to-left wipe, while the
   section is on screen; a pause button stops it. */
class Wipe extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-wipe-item]')];
    this.btn = this.querySelector('[data-pause]') || this.closest('section')?.querySelector('.v9-editorial__counter [data-pause]');
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 5000;
    if (this.items.length < 2 || reduce.matches) { if (this.btn) this.btn.hidden = true; return; }
    this.btn?.addEventListener('click', () => {
      this.paused = !this.paused;
      this.classList.toggle('is-paused', this.paused);
      this.btn.closest('.v9-editorial__counter')?.classList.toggle('is-paused', this.paused);
      this.btn.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
      this.btn.setAttribute('aria-label', this.paused ? 'Play photographs' : 'Pause photographs');
      this.tick();
    });
    // keeps moving under a resting cursor (owner: slideshows must move on their own); the pause button stops it
    new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; this.tick(); }, { threshold: 0.4 }).observe(this);
  }
  tick() {
    clearTimeout(this.timer);
    if (!this.visible || this.held || this.paused) return;
    this.timer = setTimeout(() => this.next(), this.interval);
  }
  next() {
    const prev = this.items[this.index];
    this.index = (this.index + 1) % this.items.length;
    const cur = this.items[this.index];
    this.items.forEach((it) => it.classList.remove('is-under'));
    prev.classList.remove('is-shown'); prev.classList.add('is-under');
    cur.classList.add('is-shown');
    (this.closest('section') || this).querySelectorAll('[data-wipe-count]').forEach((c) => { c.textContent = String(this.index + 1); });
    this.closest('section')?.querySelectorAll('[data-wipe-index]').forEach((el) => el.classList.toggle('is-current', Number(el.dataset.wipeIndex) === this.index));
    this.tick();
  }
}
if (!customElements.get('fp-v9-wipe')) customElements.define('fp-v9-wipe', Wipe);

/* Motion scan, one pass for the whole store (V10): unveil (photographs and films open upward as they enter),
   stagger (a band's panels one after another), reveal (headings rise 12px; moved here from fp-v8.js) and
   ripple (row cards rise one after another). It runs at load and again on anything added later - filtered
   grids, infinite scroll, rows fetched after load - through window.fpMotion.scan(root) and a watcher on <main>.
   Anything already on screen at load is never hidden; something added later on screen animates at once;
   hidden elements (zero size) are left for a later scan. */
const fpMotion = (() => {
  const off = reduce.matches || !('IntersectionObserver' in window);
  const seen = new WeakSet();
  const KINDS = [
    { sel: '[data-v9-unveil]', cls: 'v9-unveil', margin: '0px 0px -10% 0px' },
    { sel: '[data-v9-stagger]', cls: 'v9-stagger', margin: '0px 0px -12% 0px' },
    { sel: '[data-v8-reveal]', cls: 'v8-reveal', margin: '0px 0px -8% 0px' },
    { sel: '.v6-departments__rail, .v8-rail [data-rail], .v8-tabs [data-rail], [data-v9-ripple]', cls: 'v9-ripple', margin: '0px 0px -12% 0px', ripple: true },
  ];
  if (!off) for (const k of KINDS) k.io = new IntersectionObserver((entries) => { for (const e of entries) if (e.isIntersecting) { e.target.classList.add('is-in'); k.io.unobserve(e.target); } }, { rootMargin: k.margin });
  let initial = true;
  const arm = (el, k) => {
    if (seen.has(el)) return;
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) return; // hidden for now (e.g. a row that fills after load): a later scan arms it
    seen.add(el);
    if (off) return;
    const onScreen = r.top < innerHeight;
    if (initial && onScreen) return;
    if (k.ripple) [...el.children].forEach((li, i) => li.style.setProperty('--i', Math.min(i, 7)));
    el.classList.add(k.cls);
    if (onScreen) requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-in')));
    else k.io.observe(el);
  };
  const scan = (root = document) => {
    for (const k of KINDS) {
      if (root.matches?.(k.sel)) arm(root, k);
      root.querySelectorAll?.(k.sel).forEach((el) => arm(el, k));
    }
  };
  scan(document); initial = false;
  // content added later (section re-renders, infinite scroll, fetched rows); a glide's loop copies are skipped
  const main = document.querySelector('main');
  if (main && !off) {
    let queued = new Set(), pending = false;
    new MutationObserver((records) => {
      for (const rec of records) for (const n of rec.addedNodes) if (n.nodeType === 1 && !n.closest('[data-clone]')) queued.add(n);
      if (!queued.size || pending) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; const nodes = queued; queued = new Set(); nodes.forEach((n) => n.isConnected && scan(n)); });
    }).observe(main, { childList: true, subtree: true });
  }
  return { scan };
})();
window.fpMotion = fpMotion;
document.dispatchEvent(new CustomEvent('fp:motion-ready'));

/* Drift: film frames move slightly slower than the page (a few percent, never scaled). */
(() => {
  const frames = [...document.querySelectorAll('[data-v9-drift]')];
  if (!frames.length || reduce.matches) return;
  let queued = false;
  const update = () => {
    queued = false;
    const h = innerHeight;
    for (const f of frames) {
      const r = f.getBoundingClientRect();
      if (r.bottom < -100 || r.top > h + 100) continue;
      const p = (r.top + r.height / 2 - h / 2) / (h / 2 + r.height / 2); // -1 .. 1 across the screen
      f.style.setProperty('--v9-drift', `${(-p * 4).toFixed(2)}%`);
    }
  };
  const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
  update();
  // the theme may scroll the body rather than the window
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  addEventListener('resize', onScroll, { passive: true });
})();

/* Campaign hero: one designer per slide, a strip of campaign panels. The lit panel widens and moves along
   on its own (step = interval / panels); after the last panel the next designer rises in, panel by panel.
   Moving the pointer over a panel or swiping the strip lights that panel and holds for a few seconds,
   then the strip carries on by itself (it never stops under a resting cursor). Pause button and
   reduced motion stop it; it also rests while off screen or in a background tab. */
class Campaign extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.copies = [...this.querySelectorAll('[data-copy]')];
    this.credits = [...this.querySelectorAll('[data-credit]')];
    this.dots = [...this.querySelectorAll('[data-goto]')];
    this.pauseBtn = this.querySelector('[data-pause]');
    this.panels = this.slides.map((s) => [...s.querySelectorAll('[data-panel]')]);
    this.index = 0; this.lit = 0;
    this.interval = Number(this.dataset.interval) || 10000;
    this.step = Math.max(1800, Math.round(this.interval / Math.max(1, this.panels[0]?.length || 4)));
    this.style.setProperty('--v9-campaign-ms', `${this.interval}ms`);
    this.paused = reduce.matches;
    this.visible = true; this.holdUntil = 0;
    requestAnimationFrame(() => this.classList.remove('is-initial'));
    this.dots.forEach((d) => d.addEventListener('click', () => { this.go(Number(d.dataset.goto)); this.hold(6000); }));
    this.pauseBtn?.addEventListener('click', () => this.setPaused(!this.paused));
    this.panels.forEach((list, si) => list.forEach((panel, pi) => {
      panel.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse' && si === this.index) { this.light(pi); this.hold(3500); } });
      panel.addEventListener('focusin', () => { if (si === this.index) { this.light(pi); this.hold(6000); } });
    }));
    // phones: the strip scrolls sideways; whichever panel is centred is lit
    this.slides.forEach((slide, si) => {
      const strip = slide.querySelector('[data-strip]');
      let t;
      strip?.addEventListener('scroll', () => {
        if (this.autoScrolling || si !== this.index) return;
        clearTimeout(t);
        t = setTimeout(() => {
          const mid = strip.scrollLeft + strip.clientWidth / 2;
          const k = this.panels[si].reduce((best, el, i) => (Math.abs(el.offsetLeft + el.offsetWidth / 2 - mid) < Math.abs(this.panels[si][best].offsetLeft + this.panels[si][best].offsetWidth / 2 - mid) ? i : best), 0);
          this.light(k); this.hold(5000);
        }, 120);
      }, { passive: true });
      strip?.addEventListener('touchstart', () => this.hold(6000), { passive: true });
    });
    new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; this.schedule(); }, { threshold: 0.25 }).observe(this);
    document.addEventListener('visibilitychange', () => this.schedule());
    // the other designers' photographs load once the first screen has settled
    pageSettled.then(() => this.querySelectorAll('img[data-src]').forEach((img) => { if (img.dataset.srcset) img.srcset = img.dataset.srcset; img.src = img.dataset.src; img.removeAttribute('data-src'); }));
    this.setPaused(this.paused);
  }
  light(k, scroll = false) {
    const list = this.panels[this.index]; if (!list?.length) return;
    this.lit = (k + list.length) % list.length;
    list.forEach((p, i) => p.classList.toggle('is-lit', i === this.lit));
    const id = `${this.index}-${this.lit}`;
    this.credits.forEach((c) => { const on = c.dataset.credit === id; c.hidden = !on; c.classList.toggle('is-current', on); });
    if (scroll && phone.matches) {
      const strip = this.slides[this.index].querySelector('[data-strip]'); const el = list[this.lit];
      if (strip && el) { this.autoScrolling = true; strip.scrollTo({ left: el.offsetLeft - (strip.clientWidth - el.offsetWidth) / 2, behavior: reduce.matches ? 'auto' : 'smooth' }); setTimeout(() => { this.autoScrolling = false; }, 700); }
    }
  }
  go(i) {
    const n = this.slides.length; const next = (i + n) % n;
    if (next === this.index) return;
    const prev = this.slides[this.index];
    prev.classList.remove('is-active'); prev.classList.add('is-leaving');
    clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => this.slides.forEach((s, k) => { if (k !== this.index) s.classList.remove('is-leaving'); }), reduce.matches ? 0 : 1700);
    this.slides.forEach((s, k) => { const on = k === next; s.classList.toggle('is-active', on); s.toggleAttribute('inert', !on); s.setAttribute('aria-hidden', on ? 'false' : 'true'); s.querySelectorAll('a').forEach((a) => { a.tabIndex = on ? 0 : -1; }); });
    this.copies.forEach((c) => { const on = Number(c.dataset.copy) === next; c.classList.toggle('is-active', on); c.toggleAttribute('inert', !on); c.setAttribute('aria-hidden', on ? 'false' : 'true'); });
    this.dots.forEach((d, k) => d.setAttribute('aria-selected', k === next ? 'true' : 'false'));
    this.index = next;
    const strip = this.slides[next].querySelector('[data-strip]'); if (strip) strip.scrollLeft = 0;
    this.light(0);
    this.classList.remove('is-running'); void this.offsetWidth; this.schedule();
  }
  advance() {
    const list = this.panels[this.index] || [];
    if (this.lit + 1 < list.length) this.light(this.lit + 1, true);
    else if (this.slides.length > 1) this.go(this.index + 1);
    else this.light(0, true);
    this.schedule();
  }
  hold(ms) { this.holdUntil = Date.now() + ms; this.schedule(); }
  schedule() {
    clearTimeout(this.timer);
    const running = !this.paused && this.visible && !document.hidden;
    this.classList.toggle('is-running', running);
    if (!running) return;
    const wait = Math.max(this.step, this.holdUntil - Date.now());
    this.timer = setTimeout(() => this.advance(), wait);
  }
  setPaused(on) {
    this.paused = on;
    this.classList.toggle('is-paused', on);
    if (this.pauseBtn) { this.pauseBtn.setAttribute('aria-pressed', on ? 'true' : 'false'); this.pauseBtn.setAttribute('aria-label', on ? 'Play slideshow' : 'Pause slideshow'); }
    this.schedule();
  }
}
if (!customElements.get('fp-v9-campaign')) customElements.define('fp-v9-campaign', Campaign);

/* Statement: words rise into place in sequence when the line enters; small films inside the words
   load after the page has settled and play only while the line is on screen. */
class Statement extends HTMLElement {
  connectedCallback() {
    const line = this.querySelector('.v9-statement__line');
    this.videos = [...this.querySelectorAll('video[data-src]')];
    if (line && !reduce.matches) {
      let i = 0;
      const walker = document.createTreeWalker(line, NodeFilter.SHOW_TEXT);
      const texts = []; while (walker.nextNode()) texts.push(walker.currentNode);
      for (const t of texts) {
        const frag = document.createDocumentFragment();
        t.textContent.split(/(\s+)/).forEach((w) => {
          if (!w) return;
          if (/^\s+$/.test(w)) { frag.appendChild(document.createTextNode(w)); return; }
          const span = document.createElement('span'); span.className = 'v9-w'; span.style.setProperty('--i', i++); span.textContent = w; frag.appendChild(span);
        });
        t.replaceWith(frag);
      }
      // films sit in the sequence after the word before them
      line.querySelectorAll('.v9-pill').forEach((pill) => { const prev = pill.previousElementSibling; pill.style.setProperty('--i', prev ? Number(prev.style.getPropertyValue('--i')) + 0.5 : 0); });
      if (line.getBoundingClientRect().top > innerHeight) line.classList.add('v9-words');
    }
    if (reduce.matches || saveData) return;
    new IntersectionObserver(([e]) => {
      if (e.isIntersecting) line?.classList.add('is-in');
      pageSettled.then(() => this.videos.forEach((v) => {
        if (e.isIntersecting) { if (!v.src) v.src = v.dataset.src; v.play().then(() => v.closest('.v9-pill')?.classList.add('is-playing')).catch(() => {}); }
        else v.pause();
      }));
    }, { threshold: 0.3 }).observe(this);
  }
}
if (!customElements.get('fp-v9-statement')) customElements.define('fp-v9-statement', Statement);

/* Department row: drag sideways with a mouse (touch and trackpads already scroll natively); a drag never
   opens the tile under the pointer. Rows below the first screen ripple in, card after card. */
(() => {
  document.querySelectorAll('.v6-departments__rail').forEach((rail) => {
    let x0 = 0, s0 = 0, down = false, dragged = false;
    rail.addEventListener('dragstart', (e) => e.preventDefault());
    rail.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' || e.button !== 0) return; down = true; dragged = false; x0 = e.clientX; s0 = rail.scrollLeft; });
    addEventListener('pointermove', (e) => {
      if (!down) return; const dx = e.clientX - x0;
      if (!dragged && Math.abs(dx) < 6) return;
      if (!dragged) { dragged = true; rail.classList.add('is-dragging'); }
      rail.scrollLeft = s0 - dx;
    });
    addEventListener('pointerup', () => { if (!down) return; down = false; if (dragged) { rail.classList.remove('is-dragging'); rail.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); }, { capture: true, once: true }); } });
  });
  if (reduce.matches) return;
  // rows ripple in through fpMotion (above); tabs: cards shown by a tab ripple in
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('.v8-tabs [role="tab"]'); if (!tab) return;
    const rail = tab.closest('.v8-tabs')?.querySelector('[data-rail]'); if (!rail) return;
    requestAnimationFrame(() => { let i = 0; [...rail.children].forEach((li) => { if (!li.hidden) li.style.setProperty('--i', Math.min(i++, 7)); }); rail.classList.remove('v9-pop'); void rail.offsetWidth; rail.classList.add('v9-pop'); });
  });
})();

/* Cover hero: the designer's name glides sideways as slides change and the photograph rises in; the
   composition leans slightly toward the pointer; drag or swipe changes slides. Transform-only motion (no
   layout shift). Autoplay keeps going under a resting cursor; the pause button stops it. */
class Cover extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-goto]')];
    this.pauseBtn = this.querySelector('[data-pause]');
    this.index = 0; this.dir = 1;
    this.interval = Number(this.dataset.interval) || 7000;
    this.style.setProperty('--v9-cover-ms', `${this.interval}ms`);
    this.paused = reduce.matches; this.visible = true;
    this.fit(); let fw = this.clientWidth; addEventListener('resize', () => { if (this.clientWidth !== fw) { fw = this.clientWidth; this.fit(); } }, { passive: true });
    document.fonts?.ready?.then(() => this.fit());
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-initial')));
    this.dots.forEach((d) => d.addEventListener('click', () => this.go(Number(d.dataset.goto))));
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.pauseBtn?.addEventListener('click', () => this.setPaused(!this.paused));
    this.addEventListener('keydown', (e) => { if (e.target.closest('[role="tablist"]') && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) { e.preventDefault(); this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1)); this.dots[this.index]?.focus(); } });
    if (!reduce.matches) {
      // lean toward the pointer (desktop)
      this.addEventListener('pointermove', (e) => { if (e.pointerType !== 'mouse' || this.drag) return; const r = this.getBoundingClientRect(); this.style.setProperty('--mx', (((e.clientX - r.left) / r.width) * 2 - 1).toFixed(3)); });
      this.addEventListener('pointerleave', () => this.style.setProperty('--mx', '0'));
    }
    // drag / swipe
    let x0 = null, y0 = 0, dragging = false;
    this.addEventListener('pointerdown', (e) => { if (e.button !== 0 || e.target.closest('button, .quick-add, .v8-btn')) return; x0 = e.clientX; y0 = e.clientY; dragging = false; });
    addEventListener('pointermove', (e) => {
      if (x0 === null) return; const dx = e.clientX - x0;
      if (!dragging && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(e.clientY - y0)) { dragging = true; this.drag = true; this.classList.add('is-dragging'); }
      if (dragging) this.style.setProperty('--dx', `${dx}px`);
    });
    addEventListener('pointerup', (e) => {
      if (x0 === null) return; const dx = e.clientX - x0; x0 = null;
      if (!dragging) return;
      this.drag = false; this.classList.remove('is-dragging'); this.style.setProperty('--dx', '0px');
      this.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true });
      if (Math.abs(dx) > 70) this.go(this.index + (dx < 0 ? 1 : -1));
    });
    new IntersectionObserver(([en]) => { this.visible = en.isIntersecting; this.schedule(); }, { threshold: 0.25 }).observe(this);
    document.addEventListener('visibilitychange', () => this.schedule());
    pageSettled.then(() => this.querySelectorAll('img[data-src]').forEach((img) => { if (img.dataset.srcset) img.srcset = img.dataset.srcset; img.src = img.dataset.src; img.removeAttribute('data-src'); }));
    this.setPaused(this.paused);
  }
  // the large name fills the width on one line
  // one size for every name (consistent like a masthead): the size at which the longest name fills the width
  fit() {
    const w = this.clientWidth * (phone.matches ? 0.92 : 0.94);
    const els = [...this.querySelectorAll('[data-fit]')];
    els.forEach((el) => { el.style.fontSize = '100px'; });
    const widest = Math.max(...els.map((el) => el.scrollWidth || el.getBoundingClientRect().width), 1);
    const size = Math.min(phone.matches ? 72 : 200, (w / widest) * 100);
    els.forEach((el) => { el.style.fontSize = `${size.toFixed(1)}px`; });
  }
  go(i) {
    const n = this.slides.length; const next = (i + n) % n;
    if (next === this.index) return;
    this.dir = (i > this.index || (this.index === n - 1 && next === 0 && i >= n)) ? 1 : -1;
    this.style.setProperty('--dir', this.dir);
    const prev = this.slides[this.index];
    prev.classList.remove('is-active'); prev.classList.add('is-leaving');
    clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => prev.classList.remove('is-leaving'), reduce.matches ? 0 : 1200);
    this.slides.forEach((s, k) => { const on = k === next; s.classList.toggle('is-active', on); s.toggleAttribute('inert', !on); s.setAttribute('aria-hidden', on ? 'false' : 'true'); });
    this.dots.forEach((d, k) => d.setAttribute('aria-selected', k === next ? 'true' : 'false'));
    this.index = next;
    this.classList.remove('is-running'); void this.offsetWidth; this.schedule();
  }
  schedule() {
    clearTimeout(this.timer);
    const running = !this.paused && this.visible && !document.hidden;
    this.classList.toggle('is-running', running);
    if (running) this.timer = setTimeout(() => { this.dir = 1; this.style.setProperty('--dir', 1); this.go(this.index + 1); }, this.interval);
  }
  setPaused(on) {
    this.paused = on; this.classList.toggle('is-paused', on);
    if (this.pauseBtn) { this.pauseBtn.setAttribute('aria-pressed', on ? 'true' : 'false'); this.pauseBtn.setAttribute('aria-label', on ? 'Play slideshow' : 'Pause slideshow'); }
    this.schedule();
  }
}
if (!customElements.get('fp-v9-cover')) customElements.define('fp-v9-cover', Cover);

/* Glide: a designer's campaign photographs drift slowly sideways in a continuous loop; drag or swipe
   to scrub (with a little momentum), trackpads scroll it sideways; it keeps drifting under a resting
   cursor and stops with the pause button, off screen, or under reduced motion. Transform-only. */
class Glide extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.items = [...this.track.children];
    this.speed = Number(this.dataset.speed) || 26;
    this.x = 0; this.v = 0; this.paused = false; this.visible = false;
    // loop copies are real cards: the photo and Quick shop work on every pass (owner 10-07: Quick shop vanished after one
    // pass when copies were inert and stripped). Copies are hidden from screen readers and the tab order only.
    // Measure once, then add every copy in one step (measuring after each append forced a full-page layout per copy)
    this.gap = () => parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
    let lastW = 0;
    const fill = () => {
      const vw = this.clientWidth; if (!vw || vw === lastW) return; lastW = vw;
      this.track.querySelectorAll('[data-clone]').forEach((c) => c.remove());
      const gap = this.gap();
      const widths = this.items.map((el) => el.getBoundingClientRect().width);
      this.setW = widths.reduce((a, w) => a + w + gap, 0);
      const frag = document.createDocumentFragment();
      let need = vw + this.setW;
      for (let round = 0; need > 0 && round < 4; round++) this.items.forEach((el, k) => {
        if (need <= 0) return;
        const c = el.cloneNode(true); c.setAttribute('data-clone', ''); c.setAttribute('aria-hidden', 'true');
        c.querySelectorAll('[data-fp-handle]').forEach((x) => x.removeAttribute('data-fp-handle'));
        c.querySelectorAll('[id]').forEach((x) => x.removeAttribute('id'));
        c.querySelectorAll('a, button').forEach((x) => x.setAttribute('tabindex', '-1'));
        frag.appendChild(c); need -= widths[k] + gap;
      });
      this.track.appendChild(frag);
    };
    const whenSized = () => requestAnimationFrame(fill);
    if (document.readyState === 'complete') whenSized(); else addEventListener('load', whenSized, { once: true });
    let rt; addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(fill, 250); }, { passive: true });
    this.btn = this.closest('section')?.querySelector('[data-glide-pause]');
    this.btn?.addEventListener('click', () => { this.paused = !this.paused; this.btn.setAttribute('aria-pressed', this.paused ? 'true' : 'false'); this.btn.setAttribute('aria-label', this.paused ? 'Play moving photographs' : 'Pause moving photographs'); this.btn.classList.toggle('is-paused', this.paused); });
    // drag / swipe
    let down = false, last = 0, lastT = 0, moved = 0;
    this.addEventListener('pointerdown', (e) => { if (e.button !== 0 || e.target.closest('button, .quick-add')) return; down = true; moved = 0; last = e.clientX; lastT = performance.now(); this.v = 0; this.classList.add('is-grabbing'); });
    addEventListener('pointermove', (e) => { if (!down) return; const dx = e.clientX - last; const t = performance.now(); moved += Math.abs(dx); this.x += dx; this.v = dx / Math.max(1, t - lastT) * 16; last = e.clientX; lastT = t; });
    addEventListener('pointerup', () => { if (!down) return; down = false; this.classList.remove('is-grabbing'); if (moved > 6) this.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true }); });
    this.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) { e.preventDefault(); this.x -= e.deltaX; } }, { passive: false });
    let prevT = performance.now(); let running = false;
    new IntersectionObserver(([en]) => { this.visible = en.isIntersecting; if (this.visible && !running) { running = true; prevT = performance.now(); requestAnimationFrame(tick); } }, { threshold: 0 }).observe(this);
    const tick = (t) => {
      const dt = Math.min(64, t - prevT); prevT = t;
      if (!this.visible) { running = false; return; }
      if (!document.hidden) {
        if (!down) {
          if (Math.abs(this.v) > 0.05) { this.x += this.v; this.v *= 0.94; }
          else if (!this.paused && !reduce.matches) this.x -= (this.speed * dt) / 1000;
        }
        if (this.setW) { while (this.x <= -this.setW) this.x += this.setW; while (this.x > 0) this.x -= this.setW; }
        this.track.style.transform = `translate3d(${this.x.toFixed(2)}px, 0, 0)`;
      }
      requestAnimationFrame(tick);
    };
  }
}
if (!customElements.get('fp-v9-glide')) customElements.define('fp-v9-glide', Glide);

/* Collage hero: the photographs lean gently toward the pointer at their own depth (translate only).
   Nothing moves on its own, so there is no pause control. */
class Collage extends HTMLElement {
  connectedCallback() {
    if (reduce.matches) return;
    let q = false, mx = 0, my = 0;
    this.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      const r = this.getBoundingClientRect(); mx = ((e.clientX - r.left) / r.width) * 2 - 1; my = ((e.clientY - r.top) / r.height) * 2 - 1;
      if (!q) { q = true; requestAnimationFrame(() => { q = false; this.style.setProperty('--px', mx.toFixed(3)); this.style.setProperty('--py', my.toFixed(3)); }); }
    });
    this.addEventListener('pointerleave', () => { this.style.setProperty('--px', '0'); this.style.setProperty('--py', '0'); });
  }
}
if (!customElements.get('fp-v9-collage')) customElements.define('fp-v9-collage', Collage);

/* Department index hero: moves through the departments on its own; pointing at or focusing a department
   shows it at once (and the advance waits while the pointer is on the list); swipe the photograph on
   phones; pause control; reduced motion holds still. The next photograph opens from a centre slit. */
class DeptIndex extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-dept]')];
    this.frames = [...this.querySelectorAll('[data-frame]')];
    this.btn = this.querySelector('[data-pause]');
    this.index = 0; this.interval = Number(this.dataset.interval) || 5000;
    this.style.setProperty('--v9-index-ms', `${this.interval}ms`);
    this.classList.add('is-initial'); requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-initial')));
    this.paused = reduce.matches; this.onList = false; this.visible = true;
    if (reduce.matches && this.btn) this.btn.hidden = true;
    const load = () => {
      this.querySelectorAll('source[data-srcset]').forEach((el) => { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); });
      this.querySelectorAll('img[data-src]').forEach((img) => { if (img.dataset.srcset) img.srcset = img.dataset.srcset; img.src = img.dataset.src; img.removeAttribute('data-src'); });
    };
    // cinema: only the current department's film plays; the rest wait (and restart when shown)
    this.films = this.frames.map((f) => f.querySelector('fp-v9-film'));
    this.films.forEach((film, k) => film?.hold?.(k !== 0));
    // advance on film end (opt-in, data-advance="film"): the next department starts as each film finishes, so no film
    // sits frozen on its last frame; the timer stays the fallback (no film, reduced motion, Save-Data, not loaded)
    this.byFilm = this.dataset.advance === 'film' && !reduce.matches && !saveData;
    if (this.byFilm) this.films[1]?.preload?.();  // the next film is ready when its turn comes (no wait on the poster)
    if (this.byFilm) this.films.forEach((film, k) => {
      const v = film?.querySelector('video'); if (!v) return;
      v.addEventListener('playing', () => { if (k === this.index) this.schedule(true); });
      v.addEventListener('ended', () => {
        if (k !== this.index) return;
        if (this.running()) { clearTimeout(this.timer); this.timer = setTimeout(() => this.show(this.index + 1), 250); }
        else if (this.onList && !this.paused) this.replay(v); // visitor is pointing at the list: never sit frozen
      });
    });
    pageSettled.then(load);
    this.items.forEach((li, k) => {
      const a = li.querySelector('a');
      a.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { load(); this.show(k); } });
      a.addEventListener('focus', () => { load(); this.show(k); });
    });
    const list = this.querySelector('.v9-index__list');
    list.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { this.onList = true; this.schedule(); const v = this.byFilm && this.films[this.index]?.querySelector('video'); if (v && v.ended) this.replay(v); } });
    list.addEventListener('pointerleave', () => { this.onList = false; this.schedule(); });
    let x0 = null;
    const media = this.querySelector('.v9-index__media');
    media.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    media.addEventListener('touchend', (e) => { if (x0 === null) return; const dx = e.changedTouches[0].clientX - x0; x0 = null; if (Math.abs(dx) > 45) { load(); this.show(this.index + (dx < 0 ? 1 : -1)); } });
    this.btn?.addEventListener('click', () => { this.paused = !this.paused; this.classList.toggle('is-paused', this.paused); this.btn.setAttribute('aria-pressed', this.paused ? 'true' : 'false'); this.btn.setAttribute('aria-label', this.paused ? 'Play the slideshow' : 'Pause the slideshow'); this.schedule(); });
    new IntersectionObserver(([en]) => { this.visible = en.isIntersecting; this.schedule(); }, { threshold: 0.3 }).observe(this);
    document.addEventListener('visibilitychange', () => this.schedule());
    this.schedule();
  }
  show(i) {
    const n = this.frames.length; const next = (i + n) % n;
    if (next !== this.index) {
      const prev = this.frames[this.index];
      prev.classList.remove('is-active'); prev.classList.add('is-leaving');
      clearTimeout(this.leave); this.leave = setTimeout(() => this.frames.forEach((f, k) => { if (k !== this.index) f.classList.remove('is-leaving'); }), 1200);
      this.frames.forEach((f, k) => { f.classList.toggle('is-active', k === next); f.setAttribute('aria-hidden', k === next ? 'false' : 'true'); });
      this.items.forEach((li, k) => li.classList.toggle('is-active', k === next));
      // the hero itself is on screen, so the shown film can start at once (its own visibility watcher lags behind the reveal)
      this.films?.forEach((film, k) => { if (!film?.hold) return; if (k === next) { film.restart(); if (this.visible) film.visible = true; film.hold(false); } else film.hold(true); });
      if (this.byFilm) this.films[(next + 1) % n]?.preload?.();
      this.index = next;
      // phones: the names are one sideways row; keep the current one in view (row only, never the page)
      const list = this.items[next].parentElement;
      if (list.scrollWidth > list.clientWidth + 1) { const lr = list.getBoundingClientRect(); const ir = this.items[next].getBoundingClientRect(); list.scrollTo({ left: list.scrollLeft + ir.left - lr.left - (lr.width - ir.width) / 2, behavior: reduce.matches ? 'auto' : 'smooth' }); }
    }
    this.classList.remove('is-running'); void this.offsetWidth; this.schedule(true);
  }
  running() { return !this.paused && !this.onList && this.visible && !document.hidden; }
  // dissolve back to the start and play again: the poster underneath is the film's first frame, so fading the video
  // out and back in reads as a soft dissolve from the last frame to the first
  replay(v) {
    v.classList.add('is-rewinding');
    setTimeout(() => { try { v.currentTime = 0; } catch (e) { /* not seekable yet */ } v.play().catch(() => {}); v.classList.remove('is-rewinding'); }, 500);
  }
  schedule(restartBar) {
    clearTimeout(this.timer);
    const run = this.running();
    let ms = this.interval;
    const v = this.byFilm ? this.films[this.index]?.querySelector('video') : null;
    if (v) {
      const d = v.duration;
      if (d && isFinite(d)) {
        const left = Math.max(0, (d - v.currentTime) * 1000);
        ms = Math.max(600, left + 250);
        if (restartBar) { this.style.setProperty('--v9-index-ms', `${Math.round(left)}ms`); this.classList.remove('is-running'); void this.offsetWidth; }
      } else ms = this.interval * 2; // not loaded yet: 'playing' reschedules
    }
    this.classList.toggle('is-running', run);
    if (run) this.timer = setTimeout(() => this.show(this.index + 1), ms);
  }
}
if (!customElements.get('fp-v9-index')) customElements.define('fp-v9-index', DeptIndex);
